/**
 * Bundle 165 B · "Send digest now" manual trigger.
 *
 * Two-click pattern matching Bundle 163 backfill UX:
 *   1. Click button -> POST {apply: false} -> renders
 *      dry-run payload + recipient list.
 *   2. Click "Send to {N} super-admins" -> POST {apply: true}
 *      -> sends respecting 6-day dedup. Non-prod env
 *      short-circuits with a friendly message.
 *
 * `force=true` is API-only per user directive; not exposed
 * in the UI.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Mail } from 'lucide-react';
import api from '@/utils/api';
import { extractErrorMessage } from '@/utils/errorMessage';
import { toast } from 'sonner';

const SendDigestNowButton = () => {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const openDryRun = async () => {
    setOpen(true);
    setPreview(null);
    setBusy(true);
    try {
      const { data } = await api.post(
        '/admin/incidents/send-digest-now', { apply: false });
      setPreview(data);
    } catch (e) {
      toast.error(extractErrorMessage(
        e, 'Digest preview failed.'));
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        '/admin/incidents/send-digest-now', { apply: true });
      if (data?.skipped) {
        toast.info(
          `Digest not sent · ${data.reason}. Force via API only.`);
      } else {
        toast.success(
          `Digest sent to ${data.sent}·`
          + ` skipped ${data.skipped_dedup}·`
          + ` errored ${data.errored}.`);
      }
      setOpen(false);
      setPreview(null);
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Digest send failed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={openDryRun}
        className="border-primary-300 text-primary-900"
        data-testid="send-digest-now-open"
      >
        <Mail className="h-3.5 w-3.5 mr-1" />
        Send digest now
      </Button>
      <Dialog
        open={open}
        onOpenChange={(o) => { if (!o) { setOpen(false); setPreview(null); } }}
      >
        <DialogContent data-testid="send-digest-now-modal">
          <DialogHeader>
            <DialogTitle>Send weekly digest now</DialogTitle>
            <DialogDescription>
              Bundle 165. Manually triggers the weekly super-admin
              digest. Respects the 6-day dedup window and the
              per-user opt-out flag <code>preferences.no_digest_email</code>.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs text-neutral-700 space-y-2">
            {busy && !preview ? (
              <div data-testid="send-digest-now-loading">
                Rendering dry-run.
              </div>
            ) : preview ? (
              <div
                className="space-y-2"
                data-testid="send-digest-now-preview"
              >
                <div className="text-[11px] uppercase tracking-widest text-neutral-500">
                  Payload
                </div>
                <pre
                  className="rounded-md border border-neutral-200 bg-neutral-50 p-2 text-[10px] overflow-auto max-h-40"
                  data-testid="send-digest-now-payload"
                >
                  {JSON.stringify(preview.payload, null, 2)}
                </pre>
                <div>
                  Recipients:
                  {' '}
                  <span
                    className="font-mono font-semibold"
                    data-testid="send-digest-now-recipient-count"
                  >
                    {preview.recipient_count}
                  </span>
                  {' '}super-admin(s)
                </div>
                {(preview.recipients || []).length ? (
                  <ul className="text-[10px] text-neutral-500 font-mono max-h-24 overflow-auto">
                    {(preview.recipients || []).map((e) => (
                      <li key={e}>{e}</li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-neutral-500">
                    No opted-in super-admins found.
                  </div>
                )}
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => { setOpen(false); setPreview(null); }}
              data-testid="send-digest-now-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-primary-900 text-white hover:bg-primary-800"
              disabled={busy || !preview || !preview.recipient_count}
              onClick={send}
              data-testid="send-digest-now-apply"
            >
              Send to {preview?.recipient_count ?? 0}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SendDigestNowButton;
